import { useRef, useState, type KeyboardEvent } from 'react';
import { Link } from 'react-router';
import { ArtifactLink } from '../components/ArtifactLink';
import { APP_NAME, ARTIFACT_TYPE_LABELS, pageTitle } from '@thottbot/shared';
import { Logo } from '../components/Logo';
import { SearchBox } from '../components/SearchBox';
import { SiteMenu } from '../components/SiteMenu';
import { StatsLine } from '../components/StatsLine';
import { useRecent } from '../api/client';
import { relativeTime } from '../util/time';

const KIND_LABEL = {
  create: 'added',
  edit: 'edited',
  screenshot: 'screenshot for',
  comment: 'commented on',
} as const;

/**
 * The June 2009 thottbot.com landing page: centred logo, Menu + search row, a tiny stats
 * line, a NEW! line, and a menu that appears on hover (or click/keyboard) below a hot strip.
 */
export function Home() {
  const recent = useRecent();
  const [open, setOpen] = useState(false);
  const [pinned, setPinned] = useState(false);
  const toggleRef = useRef<HTMLButtonElement>(null);

  const reveal = () => setOpen(true);
  const toggle = () => {
    const next = !pinned;
    setPinned(next);
    setOpen(next);
  };
  const leave = () => {
    if (!pinned) setOpen(false);
  };
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Escape' || !open) return;
    setPinned(false);
    setOpen(false);
    toggleRef.current?.focus();
  };

  const newest = recent.data?.items[0];
  return (
    <div className="landing" onKeyDown={onKeyDown}>
      <title>{pageTitle()}</title>
      <h1 className="sr-only">{APP_NAME}</h1>
      <Link to="/" className="logo-link">
        <Logo size="large" />
      </Link>
      <SiteMenu
        open={open}
        onToggle={toggle}
        onReveal={reveal}
        onLeave={leave}
        toggleRef={toggleRef}
        searchBox={<SearchBox />}
      >
        <StatsLine tiny />
        {newest && (
          <p className="notice-new" data-testid="notice-new">
            <b className="new-label">NEW!</b> {newest.battletag} {KIND_LABEL[newest.kind]}{' '}
            <ArtifactLink type={newest.artifact.type} slug={newest.artifact.slug} name={newest.artifact.name} />
          </p>
        )}
        <div className="menu-hotstrip" onPointerEnter={reveal} aria-hidden="true" />
      </SiteMenu>
      {recent.data && recent.data.items.length > 0 && (
        <section className="section recent" data-testid="recent">
          <h2>Recent contributions</h2>
          <div className="table-scroll">
            <table className="grid">
              <thead>
                <tr>
                  <th>Who</th>
                  <th>What</th>
                  <th>When</th>
                </tr>
              </thead>
              <tbody>
                {recent.data.items.map((r, i) => (
                  <tr key={`${r.kind}-${r.artifact.slug}-${r.createdAt}-${i}`}>
                    <td>{r.battletag}</td>
                    <td>
                      {KIND_LABEL[r.kind]}{' '}
                      <ArtifactLink type={r.artifact.type} slug={r.artifact.slug} name={r.artifact.name} />{' '}
                      <span className="muted">
                        ({ARTIFACT_TYPE_LABELS[r.artifact.type].singular})
                      </span>
                    </td>
                    <td>{relativeTime(r.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}
